import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Monitor, Smartphone, Trash2, Loader2, ShieldCheck, Globe, X } from "lucide-react";
import { toast } from "sonner";

export default function DeviceManager() {
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/auth/devices").then((r) => setDevices(r.data.devices)).catch(() => setDevices([])), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (d) => {
    setBusy(d.device_id);
    try { await api.delete(`/auth/devices/${d.device_id}`); toast.success(`${d.label} signed out`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove device"); } finally { setBusy(null); }
  };
  const signOutAll = async () => {
    setBusy("all");
    try { await api.post("/auth/logout-all"); toast.success("Signed out everywhere except this device"); load(); }
    catch { toast.error("Could not sign out other devices"); } finally { setBusy(null); }
  };
  if (devices === null) return null;
  return (
    <section className="mb-10 rounded-2xl border border-slate-800 bg-[#0f1219] overflow-hidden" data-testid="device-manager">
      <div className="p-4 border-b border-slate-800 flex items-center gap-3 flex-wrap">
        <ShieldCheck className="w-5 h-5 text-cyan-400" />
        <div className="flex-1 min-w-[180px]"><p className="font-semibold">Signed-in devices</p><p className="text-xs text-slate-400">Every browser or phone currently signed in to your account.</p></div>
        {devices.length > 1 && <Button size="sm" variant="outline" onClick={signOutAll} disabled={busy === "all"} className="border-slate-700" data-testid="device-manager-sign-out-all">{busy === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign out everywhere else"}</Button>}
      </div>
      <ul className="divide-y divide-slate-800/60">
        {devices.map((d) => {
          const mobile = /iphone|ipad|android/i.test(d.ua || "");
          return (
            <li key={d.device_id || d.fp} className="p-4 flex items-center gap-3 text-sm" data-testid={`device-row-${d.device_id}`}>
              {mobile ? <Smartphone className="w-5 h-5 text-slate-400 shrink-0" /> : <Monitor className="w-5 h-5 text-slate-400 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{d.label}{d.current && <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300" data-testid="device-current-badge">This device</span>}</p>
                <p className="text-xs text-slate-500 truncate">IP {d.ip} · first seen {new Date(d.first_seen).toLocaleDateString()} · last active {new Date(d.last_seen).toLocaleString()}</p>
              </div>
              {!d.current && <Button size="icon" variant="ghost" onClick={() => remove(d)} disabled={busy === d.device_id} className="text-slate-400 hover:text-red-300" aria-label="Remove device" data-testid={`device-remove-${d.device_id}`}>{busy === d.device_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</Button>}
            </li>
          );
        })}
      </ul>
      <TrustedCountries />
    </section>
  );
}

const flag = (cc) => String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
const countryName = (cc) => { try { return new Intl.DisplayNames(["en"], { type: "region" }).of(cc); } catch { return cc; } };

function TrustedCountries() {
  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/auth/countries").then((r) => setCountries(r.data.countries)).catch(() => {}); }, []);
  const remove = async (cc) => {
    setBusy(cc);
    try { const r = await api.delete(`/auth/countries/${cc}`); setCountries(r.data.countries); toast.success(`${countryName(cc)} removed — next sign-in from there needs email confirmation`); }
    catch { toast.error("Could not remove"); } finally { setBusy(null); }
  };
  return (
    <div className="p-4 border-t border-slate-800" data-testid="trusted-countries">
      <div className="flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-cyan-400" /><p className="font-semibold text-sm">Trusted countries</p></div>
      <p className="text-xs text-slate-400 mb-3">Sign-ins from anywhere else are blocked until you confirm by email. Remove a country you no longer travel to.</p>
      {countries.length === 0 ? <p className="text-xs text-slate-500" data-testid="trusted-countries-empty">No trusted countries yet — your next sign-in location will be trusted automatically.</p> : (
        <ul className="flex flex-wrap gap-2">{countries.map((cc) => (
          <li key={cc} className="inline-flex items-center gap-2 pl-3 pr-1 h-9 rounded-full bg-[#0B0D12] border border-slate-700 text-sm" data-testid={`trusted-country-${cc}`}>
            <span>{flag(cc)}</span><span>{countryName(cc)}</span>
            <button onClick={() => remove(cc)} disabled={busy === cc} className="w-7 h-7 rounded-full inline-flex items-center justify-center text-slate-500 hover:text-red-300 hover:bg-red-500/10" aria-label={`Remove ${cc}`} data-testid={`trusted-country-remove-${cc}`}>{busy === cc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}</button>
          </li>))}</ul>
      )}
    </div>
  );
}
